import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import purchaseApi from "../../api/purchaseApi";
import vendorApi from "../../api/vendorApi";
import { getAllGoods } from "../../api/goodsApi";
import ConfirmModal from "../../components/common/ConfirmModal";
import AppDropdown from "../../components/AppDropdown";
import "../../style/ToastNotifications.css";
import "../../style/Dropdown.css";
import "./PurchaseOrderPage.css";

const today = new Date().toISOString().slice(0, 10);
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_CYLINDERS = ["Small", "Big", "CAP", "Other"];
const DEFAULT_UNITS = ["NOS", "KG", "LTR"];
const TAX_RATES = [0, 5, 12, 18, 28];

const emptyOutwardRow = () => ({
    id: uid(), 
    productId: "",
    cylinderType: "",
    quantity: "",
    unit: "NOS",
    emptyOut: "",
    supplierHolding: "",
});

const emptyInwardRow = () => ({
    id: uid(),
    productId: "",
    cylinderType: "",
    quantity: "",
    unit: "NOS",
    fullIn: "",
    emptyReturned: "",
    holding: "",
    rate: "",
    discount: "0",
    tax: "18",
});

const money = (value) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const fileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const asArray = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.results)) return response.data.results;
    if (Array.isArray(response?.results)) return response.results;
    return [];
};

function getId(value) {
    return value?.id ?? value?.value ?? value?.product_id ?? value?.productId ?? "";
}

function getName(value) {
    return (
        value?.name ??
        value?.product_name ??
        value?.productName ??
        value?.item_name ??
        value?.item ??
        value?.display_name ??
        value?.displayName ??
        ""
    );
}

function Label({ children, required = false }) {
    return (
        <label className="purchase-label">
            {children}
            {required && <span className="purchase-required">*</span>}
        </label>
    );
}

function Field({ label, required, error, children, className = "" }) {
    return (
        <div className={`purchase-field ${className}`}>
            <Label required={required}>{label}</Label>
            {children}
            {error && <span className="purchase-error">{error}</span>}
        </div>
    );
}

function Input({ className = "", ...props }) {
    return <input className={`purchase-input ${className}`} {...props} />;
}

function Textarea({ className = "", ...props }) {
    return <textarea className={`purchase-textarea ${className}`} {...props} />;
}

function Icon({ name }) {
    const common = {
        fill: "none",
        viewBox: "0 0 24 24",
        stroke: "currentColor",
        strokeWidth: 1.8,
        "aria-hidden": true,
    };

    const paths = {
        purchase: <><path strokeLinecap="round" strokeLinejoin="round" d="M7 3h8l4 4v14H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 3v5h5M9 13h6M9 17h6M9 9h2" /></>,
        supplier: <><path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V6l7-3 7 3v15M8 9h1M8 13h1M15 9h1M15 13h1M10 21v-5h4v5" /></>,
        box: <><path strokeLinecap="round" strokeLinejoin="round" d="m12 3 8 4-8 4-8-4 8-4Z" /><path strokeLinecap="round" strokeLinejoin="round" d="m4 7 8 4 8-4v10l-8 4-8-4V7ZM12 11v10" /></>,
        calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
        truck: <><path strokeLinecap="round" strokeLinejoin="round" d="M3 6h11v11H3zM14 10h4l3 3v4h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></>,
        note: <><path strokeLinecap="round" strokeLinejoin="round" d="M5 4h10l4 4v12H5z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 4v5h4M8 13h8M8 17h6" /></>,
        clip: <><path strokeLinecap="round" strokeLinejoin="round" d="M8 12.5 14.5 6a3 3 0 1 1 4.2 4.2l-7.6 7.6a5 5 0 0 1-7.1-7.1l7.1-7.1" /></>,
        plus: <><path strokeLinecap="round" d="M12 5v14M5 12h14" /></>,
        trash: <><path strokeLinecap="round" strokeLinejoin="round" d="M5 7h14M10 11v6M14 11v6M8 7l1-3h6l1 3m-9 0 1 14h10l1-14" /></>,
        arrowRight: <><path strokeLinecap="round" strokeLinejoin="round" d="M4 12h16M13 6l6 6-6 6" /></>,
        arrowLeft: <><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4m7-6-6 6 6 6" /></>,
        inventory: <><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4" /></>,
        supplierBuilding: <><path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10Z" /></>,
        info: <><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 10v6M12 7h.01" /></>,
        search: <><circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="m20 20-4-4" /></>,
        check: <><path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" /></>,
    };

    return <svg className="purchase-icon po-svg-icon" {...common}>{paths[name] || paths.info}</svg>;
}

function Card({ title, icon, children, className = "" }) {
    return (
        <section className={`purchase-card po-card ${className}`}>
            <div className="purchase-card-header po-card-header">
                <div className="purchase-card-title po-card-title">
                    {icon && <span className="purchase-card-icon po-card-icon" aria-hidden="true"><Icon name={icon} /></span>}
                    <h2>{title}</h2>
                </div>
            </div>
            <div className="purchase-card-body po-card-body">{children}</div>
        </section>
    );
}

function VendorPicker({ vendors, value, onChange, loading, error }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const close = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const selected = vendors.find((v) => String(getId(v)) === String(value));
    const filtered = vendors.filter((v) => {
        const text = `${v?.name || ""} ${v?.company || ""} ${v?.companyName || ""} ${v?.gstin || v?.gstNumber || ""}`.toLowerCase();
        return text.includes(search.toLowerCase());
    });

    return (
        <div className="vendor-picker" ref={ref}>
            <Label required>Vendor / Supplier</Label>
            <div className="vendor-search-wrap">
                <Icon name="search" />
                <input
                    className={`purchase-input vendor-search ${error ? "purchase-input-error" : ""}`}
                    value={open ? search : (selected ? getName(selected) : "")}
                    placeholder={loading ? "Loading vendors..." : "Search vendor..."}
                    disabled={loading}
                    onFocus={() => {
                        setSearch("");
                        setOpen(true);
                    }}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setOpen(true);
                    }}
                />
            </div>
            {open && (
                <div className="vendor-dropdown">
                    {filtered.length ? filtered.map((vendor) => (
                        <button
                            type="button"
                            className="vendor-option"
                            key={getId(vendor)}
                            onClick={() => {
                                onChange(String(getId(vendor)));
                                setOpen(false);
                                setSearch("");
                            }}
                        >
                            <strong>{getName(vendor) || "Unnamed Vendor"}</strong>
                            <span>{vendor?.companyName || vendor?.company || vendor?.displayName || ""}</span>
                        </button>
                    )) : <div className="vendor-empty">No vendors found</div>}
                </div>
            )}
            {error && <span className="purchase-error">{error}</span>}
        </div>
    );
}

function VendorInfo({ vendor }) {
    if (!vendor) {
        return <div className="vendor-placeholder">Select a vendor to view supplier information.</div>;
    }

    const rows = [
        ["Company", vendor?.companyName || vendor?.company || getName(vendor)],
        ["Address", vendor?.address || vendor?.information || vendor?.billingAddress || "—"],
        ["Contact", vendor?.contactPerson || vendor?.contact || vendor?.primaryContact || "—"],
        ["Phone", vendor?.phone || vendor?.mobile || vendor?.contactNumber || "—"],
        ["Email", vendor?.email || "—"],
        ["GSTIN", vendor?.gstin || vendor?.gstNumber || vendor?.GSTIN || "—"],
    ];

    return (
        <div className="vendor-info">
            <div className="vendor-info-title">Vendor Information</div>
            {rows.map(([label, value]) => (
                <div className="vendor-info-row" key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                </div>
            ))}
        </div>
    );
}

function Attachments({ files, onChange }) {
    const inputRef = useRef(null);

    const addFiles = (fileList) => {
        const accepted = Array.from(fileList || []).filter((file) =>
            /\.(pdf|jpg|jpeg|png|xlsx|doc|docx)$/i.test(file.name)
        );
        const mapped = accepted.map((file) => ({
            id: uid(),
            name: file.name,
            size: file.size,
            file,
        }));
        onChange([...files, ...mapped]);
    };

    return (
        <Card title="Attachments" icon="clip">
            <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.doc,.docx"
                className="purchase-hidden-file"
                onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                }}
            />
            <button type="button" className="upload-dropzone" onClick={() => inputRef.current?.click()}>
                <span className="upload-icon"><Icon name="clip" /></span>
                <strong>Click to upload</strong>
                <span>PDF, JPG, JPEG, PNG, XLSX, DOC, DOCX</span>
                <small>Supplier invoices, challans and supporting documents</small>
            </button>
            {!!files.length && (
                <div className="attachment-list">
                    {files.map((file) => (
                        <div className="attachment-row" key={file.id}>
                            <span className="attachment-file-icon"><Icon name="note" /></span>
                            <div className="attachment-meta">
                                <strong>{file.name}</strong>
                                <span>{fileSize(file.size)}</span>
                            </div>
                            <button type="button" className="icon-action danger" onClick={() => onChange(files.filter((f) => f.id !== file.id))} aria-label="Remove attachment">
                                <Icon name="trash" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

function SectionActions({ onCancel, onDraft, onSave, saveLabel = "Save" }) {
    return (
        <div className="purchase-actions">
            <button type="button" className="purchase-btn secondary" onClick={onCancel}>Cancel</button>
            <button type="button" className="purchase-btn secondary" onClick={onDraft}>Save Draft</button>
            <button type="button" className="purchase-btn primary" onClick={onSave}>{saveLabel}</button>
        </div>
    );
}

function Landing({ onSelect }) {
    const navigate = useNavigate();

    return (
        <div className="purchase-landing">
            <div className="purchase-landing-head">
                <span className="purchase-brand-icon"><Icon name="box" /></span>
                <div>
                    <span>Purchase</span>
                    <strong>Purchase Entry</strong>
                </div>
                <button type="button" className="purchase-back-btn" onClick={() => navigate("/dashboard/purchases/orders")}>
                    <Icon name="arrowLeft" /> Back
                </button>
            </div>
            <div className="purchase-landing-content">
                <div className="purchase-landing-copy">
                    <h1>Select Purchase Entry Type</h1>
                    <p>Choose the type of product inventory movement to record</p>
                </div>

                <div className="purchase-flow">
                    <div className="flow-node">
                        <span className="flow-node-icon"><Icon name="inventory" /></span>
                        <span>Our Inventory</span>
                    </div>
                    <div className="flow-directions">
                        <span className="flow-direction outward"><Icon name="arrowRight" />Outward</span>
                        <span className="flow-direction inward"><Icon name="arrowLeft" />Inward</span>
                    </div>
                    <div className="flow-node">
                        <span className="flow-node-icon"><Icon name="supplierBuilding" /></span>
                        <span>Supplier</span>
                    </div>
                </div>

                <div className="purchase-choice-grid">
                    <button type="button" className="purchase-choice outward" onClick={() => onSelect("outward")}>
                        <span className="choice-icon outward"><Icon name="arrowRight" /></span>
                        <strong>Purchase Outward</strong>
                        <p>Send empty/unfilled products or cylinders to supplier for filling or processing</p>
                        <span className="choice-flow outward">Inventory <Icon name="arrowRight" /> Supplier Holding</span>
                        <span className="choice-button outward">Create Outward <Icon name="arrowRight" /></span>
                    </button>
                    <button type="button" className="purchase-choice inward" onClick={() => onSelect("inward")}>
                        <span className="choice-icon inward"><Icon name="arrowLeft" /></span>
                        <strong>Purchase Inward</strong>
                        <p>Receive filled or processed products and cylinders back from supplier into inventory</p>
                        <span className="choice-flow inward">Supplier Holding <Icon name="arrowRight" /> Inventory</span>
                        <span className="choice-button inward">Create Inward <Icon name="arrowRight" /></span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function Header({ type, onBack, onSave, onDraft, onCancel }) {
    const outward = type === "outward";
    return (
        <div className="purchase-topbar po-topbar">
            <div className="purchase-topbar-inner po-topbar-inner">
                <div className="purchase-title-wrap">
                    <button type="button" className="back-button" onClick={onBack} aria-label="Back">
                        <Icon name="arrowLeft" />
                    </button>
                    <span className={`purchase-title-icon po-title-icon ${outward ? "outward" : "inward"}`} aria-hidden="true"><Icon name="purchase" /></span>
                    <div className="purchase-title-text">
                        <div>
                            <h1>Purchase {outward ? "Outward" : "Inward"}</h1>
                            <span className={`flow-badge ${outward ? "outward" : "inward"}`}>{outward ? "OUTWARD" : "INWARD"}</span>
                        </div>
                        <p>{outward ? "Send products/cylinders to supplier for filling or processing" : "Receive filled/processed products and cylinders from supplier"}</p>
                    </div>
                </div>
                <SectionActions onCancel={onCancel} onDraft={onDraft} onSave={onSave} saveLabel={outward ? "Save Outward" : "Save Inward"} />
            </div>
        </div>
    );
}

function MovementBanner({ type }) {
    const outward = type === "outward";
    return (
        <div className={`movement-banner ${outward ? "outward" : "inward"}`}>
            <Icon name="info" />
            <p>
                <strong>Inventory Effect:</strong>{" "}
                {outward
                    ? <>Product Inventory <b>decreases</b> and Supplier Holding <b>increases</b>.</>
                    : <>Supplier Holding <b>decreases</b> and Product Inventory <b>increases</b>.</>}
            </p>
        </div>
    );
}

function ProductOptions({ products }) {
    return [
        { value: "", label: "Select product..." },
        ...products.map((p) => ({
            value: String(getId(p) || getName(p)),
            label: getName(p)
        }))
    ];
}

function ProductInventoryTable({ type, rows, setRows, products, cylinders, errors }) {
    const outward = type === "outward";

    const updateRow = (id, field, value) => {
        setRows((current) => current.map((row) => {
            if (row.id !== id) return row;

            const next = { ...row, [field]: value };

            if (outward && field === "quantity") {
                next.emptyOut = value;
                next.supplierHolding = value;
            }

            if (!outward && field === "quantity" && !next.fullIn) {
                next.fullIn = value;
            }

            return next;
        }));
    };

    const addRow = () => {
        setRows((current) => [
            ...current,
            outward ? emptyOutwardRow() : emptyInwardRow(),
        ]);
    };

    const removeRow = (id) => {
        setRows((current) =>
            current.length > 1 ? current.filter((row) => row.id !== id) : current
        );
    };

    const totals = useMemo(
        () =>
            rows.reduce(
                (acc, row) => {
                    acc.qty += Number(row.quantity) || 0;

                    if (outward) {
                        acc.movement += Number(row.emptyOut) || 0;
                        acc.holding += Number(row.supplierHolding) || 0;
                    } else {
                        acc.movement += Number(row.fullIn) || 0;
                        acc.holding += Number(row.holding) || 0;
                    }

                    return acc;
                },
                { qty: 0, movement: 0, holding: 0 }
            ),
        [rows, outward]
    );

    const productOptions = ProductOptions({ products });
    const cylinderOptions = [
        { value: "", label: "Select..." },
        ...cylinders.map((c) => ({ value: c, label: c })),
    ];
    const unitOptions = DEFAULT_UNITS.map((unit) => ({
        value: unit,
        label: unit,
    }));
    const taxOptions = TAX_RATES.map((tax) => ({
        value: tax,
        label: `${tax}%`,
    }));

    const ProductDropdown = ({ row }) => (
        <div className="product-dropdown-cell">
            <AppDropdown
                value={row.productId}
                onChange={(val) => updateRow(row.id, "productId", val)}
                options={productOptions}
                placeholder="Select product..."
            />
        </div>
    );

    const CylinderDropdown = ({ row }) => (
        <AppDropdown
            value={row.cylinderType}
            onChange={(val) => updateRow(row.id, "cylinderType", val)}
            options={cylinderOptions}
            placeholder="Select..."
        />
    );

    const UnitDropdown = ({ row }) => (
        <AppDropdown
            value={row.unit}
            onChange={(val) => updateRow(row.id, "unit", val)}
            options={unitOptions}
            placeholder="Unit"
        />
    );

    const DeleteButton = ({ row }) => (
        <button
            type="button"
            className="icon-action danger"
            onClick={() => removeRow(row.id)}
            disabled={rows.length === 1}
            aria-label="Delete product"
        >
            <Icon name="trash" />
        </button>
    );

    const OutwardTable = () => (
        <div className="purchase-table-wrap purchase-table-wrap--outward">
            <table className="purchase-table purchase-table--outward">
                <colgroup>
                    <col className="po-col-product" />
                    <col className="po-col-cylinder" />
                    <col className="po-col-qty" />
                    <col className="po-col-unit" />
                    <col className="po-col-movement" />
                    <col className="po-col-holding" />
                    <col className="po-col-actions" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Cylinder Type</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Empty Out</th>
                        <th>Supplier Holding</th>
                        <th aria-label="Actions"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id}>
                            <td className="product-cell">
                                <ProductDropdown row={row} />
                            </td>
                            <td>
                                <CylinderDropdown row={row} />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    min="0"
                                    step="any"
                                    value={row.quantity}
                                    onChange={(e) =>
                                        updateRow(row.id, "quantity", e.target.value)
                                    }
                                    placeholder="0"
                                />
                            </td>
                            <td>
                                <UnitDropdown row={row} />
                            </td>
                            <td>
                                <Input
                                    className="movement-cell outward-cell"
                                    type="text"
                                    min="0"
                                    value={row.emptyOut}
                                    onChange={(e) =>
                                        updateRow(row.id, "emptyOut", e.target.value)
                                    }
                                    placeholder="0"
                                />
                            </td>
                            <td>
                                <Input
                                    className="movement-cell outward-cell readonly"
                                    value={row.supplierHolding}
                                    readOnly
                                />
                            </td>
                            <td className="actions-cell">
                                <DeleteButton row={row} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const InwardTable = () => (
        <div className="purchase-table-wrap purchase-table-wrap--inward">
            <table className="purchase-table purchase-table--inward">
                <colgroup>
                    <col className="po-col-product" />
                    <col className="po-col-cylinder" />
                    <col className="po-col-qty" />
                    <col className="po-col-unit" />
                    <col className="po-col-full" />
                    <col className="po-col-empty-returned" />
                    <col className="po-col-holding" />
                    <col className="po-col-rate" />
                    <col className="po-col-discount" />
                    <col className="po-col-tax" />
                    <col className="po-col-amount" />
                    <col className="po-col-actions" />
                </colgroup>
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Cylinder Type</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Full In</th>
                        <th>Empty Returned</th>
                        <th>Holding</th>
                        <th>Rate (₹)</th>
                        <th>Discount %</th>
                        <th>Tax</th>
                        <th>Amount</th>
                        <th aria-label="Actions"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const amount = calcRow(row);

                        return (
                            <tr key={row.id}>
                                <td className="product-cell">
                                    <ProductDropdown row={row} />
                                </td>
                                <td>
                                    <CylinderDropdown row={row} />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        min="0"
                                        step="any"
                                        value={row.quantity}
                                        onChange={(e) =>
                                            updateRow(row.id, "quantity", e.target.value)
                                        }
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <UnitDropdown row={row} />
                                </td>
                                <td>
                                    <Input
                                        className="movement-cell inward-cell"
                                        type="text"
                                        min="0"
                                        value={row.fullIn}
                                        onChange={(e) =>
                                            updateRow(row.id, "fullIn", e.target.value)
                                        }
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        min="0"
                                        value={row.emptyReturned}
                                        onChange={(e) =>
                                            updateRow(row.id, "emptyReturned", e.target.value)
                                        }
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        min="0"
                                        value={row.holding}
                                        onChange={(e) =>
                                            updateRow(row.id, "holding", e.target.value)
                                        }
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        min="0"
                                        step="0.01"
                                        value={row.rate}
                                        onChange={(e) =>
                                            updateRow(row.id, "rate", e.target.value)
                                        }
                                        placeholder="0.00"
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="text"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={row.discount}
                                        onChange={(e) =>
                                            updateRow(row.id, "discount", e.target.value)
                                        }
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <AppDropdown
                                        value={row.tax}
                                        onChange={(val) =>
                                            updateRow(row.id, "tax", val)
                                        }
                                        options={taxOptions}
                                        placeholder="Tax"
                                    />
                                </td>
                                <td className="amount-cell">{money(amount)}</td>
                                <td className="actions-cell">
                                    <DeleteButton row={row} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <Card
            title="Product Inventory Movement"
            icon="box"
            className={`inventory-movement-card ${
                outward ? "inventory-movement-card--outward" : "inventory-movement-card--inward"
            }`}
        >
            <div className="inventory-stats">
                <div className="inventory-stat accent">
                    <strong>{rows.filter((r) => r.productId).length}</strong>
                    <span>Products</span>
                </div>
                <div className="inventory-stat accent">
                    <strong>{totals.qty}</strong>
                    <span>Total Qty</span>
                </div>
                <div className="inventory-stat">
                    <strong>{totals.movement}</strong>
                    <span>{outward ? "Empty Out" : "Full In"}</span>
                </div>
                <div className="inventory-stat">
                    <strong>{totals.holding}</strong>
                    <span>{outward ? "Supplier Holding" : "Holding"}</span>
                </div>
            </div>

            {outward ? <OutwardTable /> : <InwardTable />}

            {(errors?.items || errors?.qty) && (
                <div className="purchase-inline-error">
                    {errors.items || errors.qty}
                </div>
            )}

            <button
                type="button"
                className={`add-product-btn ${outward ? "outward" : "inward"}`}
                onClick={addRow}
            >
                <Icon name="plus" /> Add Product
            </button>
        </Card>
    );
}

function calcRow(row) {
    const base = (Number(row.quantity) || 0) * (Number(row.rate) || 0);
    const discount = base * (Number(row.discount) || 0) / 100;
    const taxable = base - discount;
    const tax = taxable * (Number(row.tax) || 0) / 100;
    return taxable + tax;
}

function FinancialSummary({ rows }) {
    const summary = useMemo(() => rows.reduce((acc, row) => {
        const base = (Number(row.quantity) || 0) * (Number(row.rate) || 0);
        const discount = base * (Number(row.discount) || 0) / 100;
        const taxable = base - discount;
        const tax = taxable * (Number(row.tax) || 0) / 100;
        acc.subTotal += base;
        acc.discount += discount;
        acc.taxable += taxable;
        acc.tax += tax;
        acc.grandTotal += taxable + tax;
        return acc;
    }, { subTotal: 0, discount: 0, taxable: 0, tax: 0, grandTotal: 0 }), [rows]);

    return (
        <Card title="Financial Summary" icon="note">
            <div className="financial-summary">
                <div><span>Sub Total</span><strong>{money(summary.subTotal)}</strong></div>
                <div><span>Discount</span><strong className="muted">− {money(summary.discount)}</strong></div>
                <div><span>Taxable Amount</span><strong>{money(summary.taxable)}</strong></div>
                <div><span>Tax (GST)</span><strong className="muted">+ {money(summary.tax)}</strong></div>
                <div className="grand"><span>Grand Total</span><strong>{money(summary.grandTotal)}</strong></div>
            </div>
        </Card>
    );
}

function PurchaseEntryForm({ type, onBack }) {
    const outward = type === "outward";
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendors, setVendors] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [products, setProducts] = useState([]);
    const cylinders = DEFAULT_CYLINDERS;

    useEffect(() => {
        const fetchData = async () => {
            setLoadingVendors(true);
            try {
                const vendorsData = await vendorApi.getAll();
                setVendors(asArray(vendorsData));
            } catch (error) {
                console.error("Failed to fetch vendors:", error);
                setVendors([]);
            } finally {
                setLoadingVendors(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const productsData = await getAllGoods();
                setProducts(asArray(productsData));
            } catch (error) {
                console.error("Failed to fetch products:", error);
                setProducts([]);
            }
        };
        fetchProducts();
    }, []);

    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: "Cancel Purchase Entry",
        message: "Are you sure you want to cancel? Unsaved changes will be lost.",
        type: "warning",
        confirmText: "Continue",
        cancelText: "Keep Editing",
        onConfirm: null,
    });

    const [vendorId, setVendorId] = useState("");
    const [documentNumber, setDocumentNumber] = useState(
        `${outward ? "PO-OUT-" : "PO-IN-"}${Date.now().toString().slice(-5)}`
    );
    const [referenceNumber, setReferenceNumber] = useState("");
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [date, setDate] = useState(today);
    const [deliveryDate, setDeliveryDate] = useState("");
    const [expectedReturnDate, setExpectedReturnDate] = useState("");
    const [vehicleNumber, setVehicleNumber] = useState("");
    const [destinationAddress, setDestinationAddress] = useState("");
    const [items, setItems] = useState([outward ? emptyOutwardRow() : emptyInwardRow()]);
    const [notes, setNotes] = useState("");
    const [terms, setTerms] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const selectedVendor = vendors.find((v) => String(getId(v)) === String(vendorId));

    useEffect(() => {
        const savedDraft = localStorage.getItem(`purchaseDraft:${type}`);
        if (!savedDraft || id) return;
        try {
            const draft = JSON.parse(savedDraft);
            if (draft?.vendorId) setVendorId(draft.vendorId);
            if (draft?.items?.length) setItems(draft.items);
            if (draft?.documentNumber) setDocumentNumber(draft.documentNumber);
            if (draft?.referenceNumber) setReferenceNumber(draft.referenceNumber);
            if (draft?.purchaseOrderNumber) setPurchaseOrderNumber(draft.purchaseOrderNumber);
            if (draft?.invoiceNumber) setInvoiceNumber(draft.invoiceNumber);
            if (draft?.date) setDate(draft.date);
            if (draft?.deliveryDate) setDeliveryDate(draft.deliveryDate);
            if (draft?.expectedReturnDate) setExpectedReturnDate(draft.expectedReturnDate);
            if (draft?.vehicleNumber) setVehicleNumber(draft.vehicleNumber);
            if (draft?.destinationAddress) setDestinationAddress(draft.destinationAddress);
            if (draft?.notes) setNotes(draft.notes);
            if (draft?.terms) setTerms(draft.terms);
        } catch (error) {
            console.warn("Unable to restore purchase draft", error);
        }
    }, [type, id]);


    const validate = () => {
        const next = {};
        if (!vendorId) next.vendor = "Vendor is required.";
        if (!date) next.date = `${outward ? "Outward" : "Inward"} date is required.`;
        if (!items.length) next.items = "Add at least one product.";
        if (items.some((row) => !row.productId)) next.items = "Select a product in every row.";
        if (items.some((row) => !row.quantity || Number(row.quantity) <= 0)) next.qty = "All quantities must be positive.";
        if (items.some((row) => !row.cylinderType)) next.cylinder = "Select a cylinder type in every row.";
        setErrors(next);
        return !Object.keys(next).length;
    };

    const payload = () => ({
        vendorId: Number(vendorId),
        vendorName: getName(selectedVendor),
        vendorEmail: selectedVendor?.email || "",
        vendorAddress: selectedVendor?.address || selectedVendor?.information || "",
        purchaseType: outward ? "OUTWARD" : "INWARD",
        inwardNumber: outward ? undefined : documentNumber,
        inwardDate: outward ? undefined : date,
        referenceNumber,
        deliveryDate: outward ? undefined : deliveryDate,
        vehicleNumber,
        destinationAddress,
        notes,
        status: "pending",
        items: items.map((row) => ({
            goodsItemId: row.productId,
            productName: getName(products.find((p) => String(getId(p) || getName(p)) === String(row.productId))) || row.productId,
            cylinderType: row.cylinderType,
            quantity: Number(row.quantity) || 0,
            unit: row.unit,
            emptyOut: outward ? Number(row.emptyOut) || Number(row.quantity) || 0 : 0,
            supplierHolding: outward ? Number(row.supplierHolding) || Number(row.quantity) || 0 : 0,
            fullIn: outward ? 0 : Number(row.fullIn) || Number(row.quantity) || 0,
            emptyReturned: outward ? 0 : Number(row.emptyReturned) || 0,
            holding: outward ? 0 : Number(row.holding) || 0,
            rate: outward ? 0 : Number(row.rate) || 0,
            discount: outward ? 0 : Number(row.discount) || 0,
            tax: outward ? 0 : Number(row.tax) || 0,
            amount: outward ? 0 : Number(calcRow(row).toFixed(2)),
        })),
        subTotal: outward ? 0 : Number(summary.subTotal.toFixed(2)),
        totalDiscount: outward ? 0 : Number(summary.discount.toFixed(2)),
        totalTax: outward ? 0 : Number(summary.tax.toFixed(2)),
        grandTotal: outward ? 0 : Number(summary.grandTotal.toFixed(2)),
    });

    const summary = useMemo(() => items.reduce((acc, row) => {
        const base = (Number(row.quantity) || 0) * (Number(row.rate) || 0);
        const discount = base * (Number(row.discount) || 0) / 100;
        const taxable = base - discount;
        const tax = taxable * (Number(row.tax) || 0) / 100;
        acc.subTotal += base;
        acc.discount += discount;
        acc.tax += tax;
        acc.grandTotal += taxable + tax;
        return acc;
    }, { subTotal: 0, discount: 0, tax: 0, grandTotal: 0 }), [items]);

    // eslint-disable-next-line no-unused-vars
    const savePurchase = async () => {
        if (!validate()) return;
        setSaving(true);
        const data = payload();
        try {
            if (id) await purchaseApi.update(id, data);
            else await purchaseApi.create(data);

            if (outward) {
                const records = JSON.parse(localStorage.getItem("purchaseOutwards") || "[]");
                const savedRecord = { id: id || documentNumber, ...data, createdAt: new Date().toISOString() };
                const index = records.findIndex((r) => String(r.id) === String(savedRecord.id));
                if (index >= 0) records[index] = savedRecord;
                else records.push(savedRecord);
                localStorage.setItem("purchaseOutwards", JSON.stringify(records));
            }

            localStorage.removeItem(`purchaseDraft:${type}`);
            const toastId = toast.success(`${outward ? "Purchase Outward" : "Purchase Inward"} saved successfully.`, {
                autoClose: false,
                closeOnClick: false,
                pauseOnHover: false,
                pauseOnFocusLoss: false,
                draggable: false,
            });
            setTimeout(() => toast.dismiss(toastId), 3000);
            setTimeout(() => navigate("/dashboard/purchases/orders"), 100);
        } catch (error) {

            console.warn("Purchase API save failed, using localStorage fallback.", error);
            try {
                const key = outward ? "purchaseOutwards" : "purchaseOrders";
                const records = JSON.parse(localStorage.getItem(key) || "[]");
                const savedRecord = { id: id || documentNumber, ...data, createdAt: new Date().toISOString() };
                const index = records.findIndex((r) => String(r.id) === String(savedRecord.id));
                if (index >= 0) records[index] = { ...records[index], ...savedRecord };
                else records.push(savedRecord);
                localStorage.setItem(key, JSON.stringify(records));
                localStorage.removeItem(`purchaseDraft:${type}`);
                const toastId = toast.success(`${outward ? "Purchase Outward" : "Purchase Inward"} saved in offline mode.`, {
                    autoClose: false,
                    closeOnClick: false,
                    pauseOnHover: false,
                    pauseOnFocusLoss: false,
                    draggable: false,
                });
                setTimeout(() => toast.dismiss(toastId), 3000);
                setTimeout(() => navigate("/dashboard/purchases/orders"), 100);
            } catch (storageError) {
                console.error("Purchase localStorage fallback failed:", storageError);
                toast.error(storageError.message || "Unable to save purchase entry.");
            }

            console.error("Purchase API save failed:", error);
            let errorMessage = "Unable to save purchase entry.";
            if (error?.response) {
                errorMessage =
                    error.response.data?.message ||
                    error.response.data?.error ||
                    error.message ||
                    errorMessage;
            } else if (error?.request) {
                errorMessage = "No response from server. Please check your connection.";
            } else if (error?.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async () => {
        const draft = {
            type,
            vendorId,
            documentNumber,
            referenceNumber,
            purchaseOrderNumber,
            invoiceNumber,
            date,
            deliveryDate,
            expectedReturnDate,
            vehicleNumber,
            destinationAddress,
            items,
            notes,
            terms,
        };
        try {
            if (purchaseApi.saveDraft) await purchaseApi.saveDraft(draft);
        } catch (error) {
            console.warn("Draft API unavailable; local draft used.", error);
        }
        localStorage.setItem(`purchaseDraft:${type}`, JSON.stringify(draft));
        toast.success("Purchase entry saved as draft. Inventory was not changed.");
    };

    const cancel = () => {
        setConfirmModal({
            isOpen: true,
            title: "Cancel Purchase Entry",
            message: "Are you sure you want to cancel? Any unsaved changes will be lost.",
            type: "warning",
            confirmText: "Yes, Cancel",
            cancelText: "Keep Editing",
            onConfirm: () => navigate("/dashboard/purchases/orders"),
        });
    };

    const organizationAddress = destinationAddress;

    return (
        <div className={`purchase-entry-page ${outward ? "purchase-outward-page" : "purchase-inward-page"}`}>
            <Header
                type={type}
                onBack={onBack}
                onSave={savePurchase}
                onDraft={saveDraft}
                onCancel={cancel}
            />

            <main className="purchase-page-content">
                <div className="purchase-page-inner">
                    <MovementBanner type={type} />

                    <div className="purchase-two-column">
                        <Card title="Supplier Details" icon="supplier">
                            <VendorPicker
                                vendors={vendors}
                                value={vendorId}
                                onChange={setVendorId}
                                loading={loadingVendors}
                                error={errors.vendor}
                            />
                            <VendorInfo vendor={selectedVendor} />
                        </Card>

                        <Card title={outward ? "Outward Details" : "Inward Details"} icon="note">
                            <div className="purchase-form-grid">
                                <Field label={outward ? "Purchase Outward / Challan No." : "Purchase Inward No."}>
                                    <Input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} />
                                </Field>
                                {!outward && (
                                    <>
                                        <Field label="Purchase Order Number">
                                            <Input value={purchaseOrderNumber} onChange={(e) => setPurchaseOrderNumber(e.target.value)} />
                                        </Field>
                                        <Field label="Supplier Invoice Number">
                                            <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                                        </Field>
                                    </>
                                )}
                                <Field label="Reference Number">
                                    <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="REF-XXXX" />
                                </Field>
                                <Field label={outward ? "Outward Date" : "Inward Date"} required error={errors.date}>
                                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={errors.date ? "purchase-input-error" : ""} />
                                </Field>
                                <Field label={outward ? "Expected Return Date" : "Delivery Date"}>
                                    <Input type="date" value={outward ? expectedReturnDate : deliveryDate} onChange={(e) => outward ? setExpectedReturnDate(e.target.value) : setDeliveryDate(e.target.value)} />
                                </Field>
                                <Field label="Vehicle Number">
                                    <Input value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} placeholder="MH-12-AB-1234" />
                                </Field>
                            </div>
                        </Card>
                    </div>

                    <Card title={outward ? "Destination / Supplier Address" : "Destination Address"} icon="truck">
                        <div className="purchase-destination-grid">
                            <Field label={outward ? "Supplier Address" : "Destination Address"} className="destination-wide">
                                <Textarea value={organizationAddress || selectedVendor?.address || ""} onChange={(e) => setDestinationAddress(e.target.value)} placeholder="Enter destination address..." rows={3} />
                            </Field>
                            <div className="destination-side">
                                <div className="destination-effect">
                                    <span>Movement</span>
                                    <strong>{outward ? "Our Inventory → Supplier" : "Supplier → Our Inventory"}</strong>
                                </div>
                            </div>
                        </div>
                    </Card>

                    <ProductInventoryTable
                        type={type}
                        rows={items}
                        setRows={setItems}
                        products={products}
                        cylinders={cylinders}
                        errors={errors}
                    />

                    {!outward && <FinancialSummary rows={items} />}

                    <div className="purchase-notes-grid">
                        <Card title="Notes" icon="note">
                            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Enter notes..." />
                        </Card>
                        <Card title="Terms & Conditions" icon="check">
                            <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={4} placeholder="Enter terms and conditions..." />
                        </Card>
                    </div>

                    <Attachments files={attachments} onChange={setAttachments} />

                    <div className="purchase-bottom-actions">
                        <SectionActions
                            onCancel={cancel}
                            onDraft={saveDraft}
                            onSave={savePurchase}
                            saveLabel={saving ? "Saving..." : (outward ? "Save Outward" : "Save Inward")}
                        />
                    </div>
                </div>
            </main>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                onConfirm={() => {
                    const action = confirmModal.onConfirm;
                    setConfirmModal((m) => ({ ...m, isOpen: false }));
                    if (action) action();
                }}
                onCancel={() => setConfirmModal((m) => ({ ...m, isOpen: false }))}
            />
        </div>
    );
}

function PurchaseOrderPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedType, setSelectedType] = useState(null);

    // Resolve the purchase movement directly from the URL so the form is
    // rendered on the first pass for /inward and /outward routes.
    const routeType = useMemo(() => {
        const path = location.pathname.toLowerCase();
        if (path.includes("/purchases/outward") || path.includes("/purchase-orders/outward")) {
            return "outward";
        }
        if (path.includes("/purchases/inward") || path.includes("/purchase-orders/inward")) {
            return "inward";
        }
        return null;
    }, [location.pathname]);

    const type = routeType || selectedType;

    const handleSelect = (nextType) => {
        setSelectedType(nextType);
        navigate(`/dashboard/purchases/${nextType}`);
    };

    const handleBack = () => {
        setSelectedType(null);
        navigate("/dashboard/purchases/orders/new");
    };

    if (!type) {
        return <Landing onSelect={handleSelect} />;
    }

    return <PurchaseEntryForm type={type} onBack={handleBack} />;
}

export default PurchaseOrderPage;
